import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import {
  HasDisengageContext,
  HasEngageContext,
  HasEventContext,
  HasMeasureContext,
  HasPointCUASContext,
  HasQrCodeContext,
} from '@components/map/contextMenu/builders/common';
import { DefaultContextMenuContext, DefaultContextMenuCore } from '@components/map/contextMenu/builders/default';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { useAppSelector } from '@hooks/redux.hooks';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { usePrevious } from '@hooks/usePrevious';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole, hasSensorRoleFromActiveProfile, selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { updateSelectedCameraKey } from '@redux/global/global.reducer';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import {
  selectIsNeutralSensorJamming,
  selectMovableCameraActiveMonitoringSensors,
} from '@redux/situation/monitoring.selectors';
import { selectNeutralInfo, selectSituationTime } from '@redux/situation/situation.selectors';
import { usePrepareEngagedPositionMutation } from '@services/c2/engagement.api';
import { isOpenedPopupByType } from '@utils/popup.utils';
import { getNeutralInfoCoordinates, isPositionEngaged } from '@utils/sensors/neutral/neutral.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { toCoordinates } from '@utils/validation/coordinates.utils';
import { isEqual } from 'lodash';
import { LngLat } from 'mapbox-gl';
import { useCallback, useEffect, useState } from 'react';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { EventPopupData, PopupTypeEnum } from '@/types/popup.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

export type Props = {
  clickPosition: { x: number; y: number };
  position: LngLat | undefined;
  onClose: () => void;
};

export function DefaultContextMenu({ clickPosition, position, onClose }: Readonly<Props>) {
  const { mapId } = useMapContext();
  const accessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));
  const accessCamCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.CAMERA }),
  );
  const canResetSituation = useAppSelector((state) => hasRole(state, RoleEnum.MANAGE_SITUATION));
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const movableLadCameras = useAppSelector(selectMovableCameraActiveMonitoringSensors);
  const updateGlobalSelectedCameraKey = useWithDispatch(updateSelectedCameraKey);
  const useCamera = useCameraCommand({ monitoringSensor: null });

  const neutralSensorAction = { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.NEUTRAL };
  const accessNeutralCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, neutralSensorAction));
  const neutralInfo = useSelectorWithReplayMode(selectNeutralInfo);
  const engagedCoordinates = getNeutralInfoCoordinates(neutralInfo);
  const previousEngagedCoordinates = usePrevious(engagedCoordinates, engagedCoordinates);
  const isEngagedPosition = isPositionEngaged(neutralInfo);
  const isNeutralSensorJamming = useSelectorWithReplayMode(selectIsNeutralSensorJamming);
  const situationTime = useAppSelector(selectSituationTime);
  const [prepareEngagedPosition] = usePrepareEngagedPositionMutation();
  const [isLoadingPreparePosition, setIsLoadingPreparePosition] = useState(false);
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const isNonActiveUserJamming =
    isNeutralSensorJamming && !!neutralInfo?.login && activeUserLogin !== neutralInfo.login;

  const handleCreateEventClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          newEvent: {
            coordinates: toCoordinates(position),
            date: new Date().toISOString(),
          },
        } as EventPopupData,
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, position, updatePopup]);

  const handleLookAt = (cameraConfiguration: CameraConfiguration) => {
    if (position) {
      useCamera.lookAt(
        {
          latitude: position.lat,
          longitude: position.lng,
          type: '2D',
        },
        null,
        null,
        null,
        null,
        true,
        cameraConfiguration,
      );
      updateGlobalSelectedCameraKey(getSensorUniqueKey(cameraConfiguration));
      onClose();
    }
  };

  const openGpsQrCodePopup = () => {
    if (position) {
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.GPS_QR_CODE,
          data: {
            coordinates: { latitude: position.lat, longitude: position.lng, type: '2D' },
          },
          position: { x: clickPosition.x, y: clickPosition.y },
        },
      });
      onClose();
    }
  };

  const handleResetSituationClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.RESET_SITUATION,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: null,
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, updatePopup]);

  const handleEngagement = () => {
    if (!accessNeutralCommand || isLoadingPreparePosition || isNonActiveUserJamming) {
      return;
    }
    setIsLoadingPreparePosition(true);
    prepareEngagedPosition(toCoordinates(position));
  };

  const handleDisengagement = () => {
    if (
      !accessNeutralCommand ||
      isLoadingPreparePosition ||
      !isEngagedPosition ||
      isNeutralSensorJamming ||
      isNonActiveUserJamming
    ) {
      return;
    }
    setIsLoadingPreparePosition(true);
    prepareEngagedPosition(null);
  };

  useEffect(() => {
    if (!neutralInfo?.targetId && !isEqual(engagedCoordinates, previousEngagedCoordinates)) {
      setIsLoadingPreparePosition(false);
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.ENGAGEMENT,
          position: { x: clickPosition.x, y: clickPosition.y },
          data: {
            openingDate: situationTime ? new Date(situationTime) : new Date(),
          },
        },
      });
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clickPosition.x,
    clickPosition.y,
    engagedCoordinates,
    mapId,
    neutralInfo?.targetId,
    onClose,
    previousEngagedCoordinates,
    updatePopup,
  ]);

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: toCoordinates(position),
    measureOrigin: MeasureOriginEnum.POINT,
  });

  const measureContext: HasMeasureContext = {
    position: position ?? null,
    label,
    enableMeasure,
  };
  const pointCUASContext: HasPointCUASContext = {
    position: position ?? null,
    accessCamCommand,
    movableLadCameras,
    handleLookAt,
  };
  const engagementContext: HasEngageContext = {
    isEngagementOptionHidden: !accessNeutralCommand,
    isEngagementOptionLoading: isLoadingPreparePosition,
    isEngagementOptionDisabled: isLoadingPreparePosition || isNonActiveUserJamming,
    handleEngagement,
  };
  const disengagementContext: HasDisengageContext = {
    isDisengagementOptionHidden: !accessNeutralCommand || !isEngagedPosition,
    isDisengagementOptionLoading: isLoadingPreparePosition,
    isDisengagementOptionDisabled: isLoadingPreparePosition || isNeutralSensorJamming || isNonActiveUserJamming,
    handleDisengagement,
  };
  const defaultCoreContext: DefaultContextMenuCore = {
    canResetSituation,
    handleResetSituationClick,
  };
  const qrCodeContext: HasQrCodeContext = {
    position: position ?? null,
    openGpsQrCodePopup,
  };
  const eventContext: HasEventContext = {
    popups,
    accessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };

  const ctx: DefaultContextMenuContext = {
    ...defaultCoreContext,
    ...qrCodeContext,
    ...eventContext,
    ...measureContext,
    ...pointCUASContext,
    ...engagementContext,
    ...disengagementContext,
  };

  const options = buildContextMenuFromType<FeatureTypeToContextComponentEnum.DEFAULT>(
    FeatureTypeToContextComponentEnum.DEFAULT,
    mapId,
    ctx,
  );

  return <MenuList menuItems={options} />;
}
